import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { orpc } from "@/lib/orpc";
import { searchPatch, optionalSearch } from "@/lib/search";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { DishSheet } from "./-components/dish-sheet";
import { IngredientLine, type KitchenIngredient } from "./-components/ingredient-line";
import { IngredientSheet } from "./-components/ingredient-sheet";
import { MenusPager } from "./-components/menus-pager";
import { useKitchen } from "./-lib/use-kitchen";
import { usePaging } from "./-lib/use-paging";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/_authenticated/menus/ingredients")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  component: IngredientsPage,
});

/** The ingredients the dishes use, library and our own, each with the dishes that use it. Typing
 * searches those first by the names the dishes give them, then the rest of the standard list. */
function IngredientsPage() {
  const { facilityId, canManage } = useKitchen();
  const { q } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => patch({ q: next }));
  const [dishId, setDishId] = useState<string | null>(null);
  const [opened, setOpened] = useState<KitchenIngredient | null>(null);
  const paging = usePaging();
  const query = q ?? "";
  const searching = query.length >= 2;
  const page = { limit: paging.size, offset: paging.offset };
  const listed = useQuery({
    ...orpc.agents.menus.ingredients.list.queryOptions({ input: { facilityId, ...page } }),
    enabled: !searching,
    placeholderData: keepWhileSame({ facilityId }),
  });
  const found = useQuery({
    ...orpc.agents.menus.ingredients.search.queryOptions({
      input: { facilityId, search: query, ...page },
    }),
    enabled: searching,
    placeholderData: keepWhileSame({ facilityId }),
  });
  const read = searching ? found : listed;
  const rows: KitchenIngredient[] = read.data?.rows ?? [];
  const groups = searching
    ? [
        { heading: "In your dishes", rows: rows.filter((row) => row.usedIn > 0) },
        { heading: "Other foods", rows: rows.filter((row) => row.usedIn === 0) },
      ]
    : [{ heading: null, rows }];

  const line = (ingredient: KitchenIngredient) => (
    <IngredientLine
      key={ingredient.id ?? ingredient.name}
      facilityId={facilityId}
      ingredient={ingredient}
      canManage={canManage}
      onOpen={setOpened}
    />
  );

  return (
    <section className="space-y-4">
      <SearchField
        value={search}
        onChange={(value) => {
          setSearch(value);
          paging.reset();
        }}
        placeholder="Search ingredients: onion, whole milk, ground beef…"
        label="Search ingredients"
        className="max-w-xl sm:w-full"
      />

      {read.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : read.isError ? (
        <ReadFailed
          message="Couldn't load the ingredients."
          error={read.error}
          onRetry={() => void read.refetch()}
        />
      ) : rows.length === 0 ? (
        searching ? (
          <EmptyState
            title={`No ingredient matches "${query}"`}
            description="Try the plain name a store would use, like whole milk or ground beef."
          />
        ) : (
          <EmptyState
            title="No ingredients yet"
            description="An ingredient shows up here once a dish lists it. Add a dish to start the list."
            action={
              <Button asChild variant="outline">
                <Link to="/menus/dishes">Open dishes</Link>
              </Button>
            }
          />
        )
      ) : (
        <div className="bg-card shadow-card divide-y overflow-hidden rounded-xl border">
          {groups.map((group) =>
            group.rows.length > 0 ? (
              <div key={group.heading ?? "all"}>
                {group.heading ? (
                  <h2 className="text-muted-foreground border-b px-4 py-1.5 text-xs font-medium">
                    {group.heading}
                  </h2>
                ) : null}
                <ul className="divide-y">{group.rows.map(line)}</ul>
              </div>
            ) : null,
          )}
          <MenusPager
            paging={paging}
            total={read.data?.total ?? 0}
            noun={["ingredient", "ingredients"]}
          />
        </div>
      )}

      {opened ? (
        <IngredientSheet
          ingredient={opened}
          onOpenDish={(id) => {
            setOpened(null);
            setDishId(id);
          }}
          onClose={() => setOpened(null)}
        />
      ) : null}

      {dishId ? (
        <DishSheet
          facilityId={facilityId}
          target={{ kind: "dish", dishId }}
          canManage={canManage}
          onClose={() => setDishId(null)}
        />
      ) : null}
    </section>
  );
}
